import React, { useEffect, useMemo, useRef } from 'react';

import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import IconLock from '@pkg/assets/images/Lock.png';
import { CircleButton, YatMagnetism } from '@pkg/components';
import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { useAppDispatch, useMobile } from '@pkg/hooks';
import { handleKeyDown } from '@pkg/utils';

import {
    hide2faPostPurchasePopup,
    show2faSetupFlow,
    toggle2faArtifactMintingPopup,
    toggle2faMintingPopup,
} from '@app/store/slices';

import './TwoFactorModalPopup.scss';

export enum TwoFactorPopupType {
    PostPurchase,
    Minting,
    ArtifactMinting,
}

export interface TwoFactorModalPopupProps {
    type?: TwoFactorPopupType;
}

export function TwoFactorModalPopup({ type = TwoFactorPopupType.PostPurchase }: TwoFactorModalPopupProps) {
    const { t: translate } = useTranslation(['twoFactorAuth', 'artifacts']);

    const { isMobile } = useMobile();
    const dispatch = useAppDispatch();

    const popupRef = useRef(null);

    const onClosePopup = () => {
        switch (type) {
            case TwoFactorPopupType.PostPurchase:
                dispatch(hide2faPostPurchasePopup());
                break;
            case TwoFactorPopupType.Minting:
                dispatch(toggle2faMintingPopup(false));
                break;
            case TwoFactorPopupType.ArtifactMinting:
                dispatch(toggle2faArtifactMintingPopup(false));
        }
    };

    const onSetup = () => {
        dispatch(show2faSetupFlow());
        switch (type) {
            case TwoFactorPopupType.PostPurchase:
                dispatch(hide2faPostPurchasePopup());
                break;
            case TwoFactorPopupType.Minting:
                localStorage.setItem('mintingAttempted', '1');
                dispatch(toggle2faMintingPopup(false));
                break;
            case TwoFactorPopupType.ArtifactMinting:
                localStorage.setItem('artifactMintingAttempted', '1');
                dispatch(toggle2faArtifactMintingPopup(false));
        }
    };

    useEffect(() => {
        if (!isMobile || !popupRef.current) {
            return;
        }

        const tl = gsap.timeline({
            repeat: 0,
            defaults: {
                duration: 0.5,
                delay: 0.2,
                ease: 'circ.easeIn',
            },
        });

        gsap.set(popupRef.current, { yPercent: 80, opacity: 0.3, delay: 0 });

        tl.addLabel('pushUp').fromTo(
            popupRef.current,
            { yPercent: 80, opacity: 0.3 },
            { yPercent: 0, opacity: 1 },
            'pushUp',
        );
    }, [isMobile, popupRef]);

    const copy = useMemo(() => {
        const copy = {
            title: translate('2fa_title'),
            text: '',
        };

        switch (type) {
            case TwoFactorPopupType.PostPurchase:
                {
                    copy.title = translate('2fa_title_yat');
                    copy.text = translate('2fa_instruction');
                }
                break;
            case TwoFactorPopupType.Minting:
                {
                    copy.title = translate('2fa_title_yat');
                    copy.text = translate('2fa_instruction_minting', { item: translate('translation:yat') });
                }
                break;

            case TwoFactorPopupType.ArtifactMinting:
                {
                    copy.text = translate('2fa_instruction_minting', { item: translate('artifacts:artifact') });
                }
                break;
        }

        return copy;
    }, [type, translate]);

    return (
        <>
            <div className="two-factor-purchase-backdrop" />
            <div className="two-factor-purchase-popup" ref={popupRef}>
                <div className="two-factor-purchase-popup__container">
                    <div className="two-factor-purchase-popup__container-content">
                        {!isMobile && (
                            <YatMagnetism classes="two-factor-purchase-popup__close">
                                <CircleButton
                                    onClick={onClosePopup}
                                    ariaLabel={translate('common:back_to_previous_page')}
                                    size="large"
                                >
                                    <CloseIcon className="page-top-bar__icon" />
                                </CircleButton>
                            </YatMagnetism>
                        )}
                        <div className="two-factor-purchase-popup__icon">
                            <img src={IconLock} alt="lock" />
                        </div>
                        <h2
                            className="two-factor-purchase-popup__title"
                            dangerouslySetInnerHTML={{ __html: copy.title }}
                        />
                        <p
                            className="two-factor-purchase-popup__instruction"
                            dangerouslySetInnerHTML={{ __html: copy.text }}
                        />
                        <div className="two-factor-purchase-popup__submit">
                            <DepButton display={DepButtonDisplay.minWidth} onClick={onSetup}>
                                {translate('2fa_submit_cta')}
                            </DepButton>
                        </div>
                        <div
                            tabIndex={0}
                            aria-label={translate('2fa_cancel_cta')}
                            onClick={onClosePopup}
                            className="two-factor-purchase-popup__cancel"
                            onKeyDown={(e) => handleKeyDown(e, ['Enter', ' '], onClosePopup)}
                        >
                            {translate('2fa_cancel_cta')}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
